// versionCheck — "is this tab running the latest deploy?"
//
// Why (Sep 15, 2026): the portal is a single-page app. A tab left open across
// a deploy keeps running the OLD bundle indefinitely — navigating between
// pages never re-fetches index.html — so a feature that is provably live for
// everyone else is invisible to that one person until they reload. This has
// bitten repeatedly (Shannon's fake-upload fix Jul 15, Hennie's softphone,
// Jennifer's warranty attachments today). chunkReload.ts only helps when a
// LAZY chunk 404s; a stale main bundle never trips it.
//
// Mechanism: every hashed build ships as /assets/index-<hash>.js and
// index.html references exactly one of them. We remember the hash this tab
// booted with, then re-fetch index.html (no-store — Netlify serves it
// must-revalidate anyway) on a slow interval and whenever the tab regains
// focus/visibility. A different hash ⇒ a newer deploy is live ⇒ the banner
// offers a Reload. We never reload on our own: a user may be mid-form or
// mid-call.

import { useEffect, useRef, useState } from "react";

const CHECK_INTERVAL_MS = 5 * 60_000;   // background poll
const MIN_GAP_MS = 45_000;              // throttle focus/visibility re-checks
const FIRST_CHECK_DELAY_MS = 60_000;    // the page just loaded — it IS current

const BUNDLE_RE = /assets\/index-([A-Za-z0-9_-]+)\.js/;

/** Hash of the main bundle THIS tab is running, or null outside a built app (dev server). */
export function currentBundleHash(): string | null {
  if (typeof document === "undefined") return null;
  const scripts = Array.from(document.querySelectorAll('script[type="module"][src]'));
  for (const s of scripts) {
    const m = (s.getAttribute("src") || "").match(BUNDLE_RE);
    if (m) return m[1];
  }
  return null;
}

/** Hash of the main bundle the server is serving right now, or null if unreadable. */
export async function fetchLiveBundleHash(): Promise<string | null> {
  try {
    const res = await fetch(`/?__v=${Date.now()}`, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { accept: "text/html" },
    });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(BUNDLE_RE);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

export interface UpdateState {
  /** True once the server is serving a different main bundle than this tab loaded. */
  updateAvailable: boolean;
  liveHash: string | null;
  currentHash: string | null;
}

/**
 * React hook: polls for a newer deploy. Safe to mount once (Layout). Returns
 * `updateAvailable=true` and keeps it true — a deploy can't be un-shipped.
 */
export function useUpdateAvailable(): UpdateState {
  const [state, setState] = useState<UpdateState>({
    updateAvailable: false,
    liveHash: null,
    currentHash: currentBundleHash(),
  });
  const lastCheckRef = useRef<number>(Date.now());
  const inFlightRef = useRef(false);

  useEffect(() => {
    const current = currentBundleHash();
    if (!current) return; // dev server / unexpected markup — nothing to compare against

    let cancelled = false;

    async function check(force = false) {
      if (cancelled || inFlightRef.current) return;
      if (!force && Date.now() - lastCheckRef.current < MIN_GAP_MS) return;
      inFlightRef.current = true;
      try {
        const live = await fetchLiveBundleHash();
        lastCheckRef.current = Date.now();
        if (cancelled || !live) return;
        setState((prev) => ({
          updateAvailable: prev.updateAvailable || live !== current,
          liveHash: live,
          currentHash: current,
        }));
      } finally {
        inFlightRef.current = false;
      }
    }

    const first = window.setTimeout(() => void check(true), FIRST_CHECK_DELAY_MS);
    const interval = window.setInterval(() => void check(true), CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    const onFocus = () => void check();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return state;
}
